/**
 * Placement for a card anchored to a point on the map.
 *
 * The card sits beside the marker rather than on top of it, flips to the other
 * side when it would leave the viewport, and is clamped so it always stays
 * fully inside the map.
 *
 * Kept pure so the geometry is testable without a map: the caller supplies the
 * projected marker point and the measured sizes.
 */

export interface AnchorInput {
  /** Projected marker position, in container pixels. */
  point: { x: number; y: number };
  card: { width: number; height: number };
  container: { width: number; height: number };
  /** Horizontal gap between marker and card. */
  gap?: number;
  /** Minimum gap to the container edges. */
  margin?: number;
  /**
   * Chrome floating over the container, per edge, in container pixels.
   *
   * The card is clamped inside the container LESS these, not inside the
   * container. Under the COP's tactical shell the map canvas is deliberately
   * larger than the visible map, so clamping to the canvas puts the selected
   * aircraft's chip - its callsign and the two numbers that decide whether the
   * sortie continues - underneath the command bar, for exactly the aircraft
   * flying low on the picture that the operator just clicked.
   *
   * All zero, the default, is the classic shell and every other caller.
   */
  insets?: { top: number; right: number; bottom: number; left: number };
}

export interface AnchorResult {
  left: number;
  top: number;
  /** Which side of the marker the card ended up on. */
  side: 'right' | 'left';
}

const DEFAULT_GAP = 28;
const DEFAULT_MARGIN = 12;

const NO_INSETS = { top: 0, right: 0, bottom: 0, left: 0 };

export function anchoredCardPosition({
  point,
  card,
  container,
  gap = DEFAULT_GAP,
  margin = DEFAULT_MARGIN,
  insets = NO_INSETS,
}: AnchorInput): AnchorResult {
  // The box the card must stay inside: the container less whatever floats over
  // it. Identical to the container when nothing does.
  const minLeft = insets.left + margin;
  const minTop = insets.top + margin;
  const rightEdge = container.width - insets.right - margin;
  const bottomEdge = container.height - insets.bottom - margin;

  // Prefer the right of the marker; flip left only when the card would overflow
  // and there is genuinely more room on the other side.
  const rightLeft = point.x + gap;
  const overflowsRight = rightLeft + card.width > rightEdge;
  const leftLeft = point.x - gap - card.width;
  const fitsLeft = leftLeft >= minLeft;

  const side: 'right' | 'left' = overflowsRight && fitsLeft ? 'left' : 'right';

  const maxLeft = Math.max(minLeft, rightEdge - card.width);
  const left = Math.min(Math.max(side === 'left' ? leftLeft : rightLeft, minLeft), maxLeft);

  // Centre on the marker vertically, then keep the whole card in the visible box.
  const maxTop = Math.max(minTop, bottomEdge - card.height);
  const top = Math.min(Math.max(point.y - card.height / 2, minTop), maxTop);

  return { left, top, side };
}
